import React, { useCallback, useContext, useState } from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';
import StepModal from '@swyftx/aviary/molecules/StepModal';
import { StepModalContext } from '@swyftx/aviary/molecules/StepModal/StepModal.context';
import { ProtectionOrderVariant } from '@swyftx/margin/build/types';
import { UpdateMarginPosition } from '@swyftx/margin/build/types';

import { useMarginInstrumentDetails } from 'src/lib/margin/hooks/instruments';
import { useUpdateMarginPosition } from 'src/lib/margin/hooks/positions';
import { useMarginPositionDetails } from 'src/lib/margin/hooks/positions/useMarginPositionDetails';
import { MarginPositionDetailsStep } from 'src/lib/margin/types';

type Props = {
  positionId?: string;
  protectionOrderType?: ProtectionOrderVariant;
  modalPosition?: ModalProps['position'];
  onClose: () => void;
};

const MarginPositionDetailsRemoveProtectionStep: React.FC<Props> = ({
  onClose,
  positionId,
  protectionOrderType,
  modalPosition,
}) => {
  const { position, positionMarket } = useMarginPositionDetails({ positionId });
  const { symbol } = useMarginInstrumentDetails({ instrument: positionMarket });
  const [loading, setLoading] = useState<boolean>(false);
  const { updatePosition, invalidateCache } = useUpdateMarginPosition();
  const { addSnackBar } = useSnackBars();
  const { onBack, stepCount } = useContext(StepModalContext);

  const closeModal = useCallback(() => {
    if (stepCount > 1) {
      onBack();
    } else {
      onClose();
    }
  }, [onBack, onClose, stepCount]);

  const onUpdatePosition = useCallback(async () => {
    if (!position) return;

    try {
      setLoading(true);

      const positionUpdate: UpdateMarginPosition = {
        takeProfit: protectionOrderType === ProtectionOrderVariant.TakeProfit ? '0' : undefined,
        stopLoss: protectionOrderType === ProtectionOrderVariant.StopLoss ? '0' : undefined,
      };

      await updatePosition(position.positionId, positionUpdate);

      addSnackBar({
        variant: 'success',
        content: `${symbol} ${position.sell ? 'short' : 'long'} position updated`,
        showIcon: true,
        duration: 5_000,
      });
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: `Failed to update ${symbol} position`,
        showIcon: true,
        label: 'View order',
        duration: 5_000,
      });

      invalidateCache();
    } finally {
      setLoading(false);
      closeModal();
    }
  }, [addSnackBar, closeModal, invalidateCache, position, protectionOrderType, symbol, updatePosition]);

  return (
    <StepModal.Step
      key={MarginPositionDetailsStep.RemoveProtectionOrder}
      onClose={onClose}
      hideActions
      position={modalPosition}
      title='Remove protection order'
    >
      <FlexLayout direction='column' spacing={24} className='px-16 pb-16'>
        <Body>This action cannot be undone. Are you sure you would like to remove this order?</Body>

        <FlexLayout spacing={16}>
          <Button variant='outlined' size='lg' onClick={closeModal} className='w-full'>
            Cancel
          </Button>
          <Button
            color='destructive'
            size='lg'
            loading={loading}
            disabled={loading}
            onClick={onUpdatePosition}
            className='w-full'
          >
            Yes, remove
          </Button>
        </FlexLayout>
      </FlexLayout>
    </StepModal.Step>
  );
};

export { MarginPositionDetailsRemoveProtectionStep };
